import { useIntl } from "react-intl";
import FormComplete from "../FormsV2/FormComplete";
import "./CallManager.less";
import {
   Icon_AddNote,
   Icon_AddTask,
   Icon_AddUserToCall,
   Icon_Bluetooth,
   Icon_CallUser,
   Icon_ContinueCall,
   Icon_ConversationTopic,
   Icon_Details,
   Icon_GoBack,
   Icon_HangUpPhoneCall,
   Icon_ManageUsers,
   Icon_MapMarker,
   Icon_MergeCalls,
   Icon_Microphone,
   Icon_MoreActions,
   Icon_MuteMicrophone,
   Icon_NIP,
   Icon_OnHold,
   Icon_PhoneCallUser,
   Icon_Reschedule,
   Icon_Speaker,
   Icon_SwapCalls,
} from "../BP2Components/Icons/icons";
import { Modal } from "../BP2Components";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getDataFromFormTabs } from "../../util/getDataFromFormTabs";
import { ParticipantList } from "./ParticipantList";
import {
   CallManagerButton,
   CallManagerInfo,
   CallManagerProps,
   CallStep,
   FormIds,
   PhoneCallStatus,
} from "./types";
import { ActionButton, ButtonGroup } from "./ButtonGroup";
import clsx from "clsx";
import { AddNewParticipantToPhoneCall } from "./AddParticipantToPhoneCall";
import { AddSummaryForm } from "./AddSummaryForm";
import Client from "./client.png";

export const CallManager = ({ tabs, parentId }: CallManagerProps) => {
   const [isCallManagerOpen, setIsCallManagerOpen] = useState<boolean>(true);
   const [formIds, setFormIds] = useState<FormIds>({ formId: null, dataId: null });
   const [activeButtons, setActiveButtons] = useState({
      moreActions: false,
      muteMicrophone: false,
      suspendPhoneCall: false,
      speaker: false,
   });
   const [callStatus, setCallStatus] = useState<PhoneCallStatus>(PhoneCallStatus.ACTIVE);
   const [callStep, setCallStep] = useState<CallStep>(CallStep.PLANNED);

   const [isMoreUserActionsOpen, setIsMoreUserActionsOpen] = useState<boolean>(false);
   const [isParticipantListOpen, setIsParticipantListOpen] = useState<boolean>(false);
   const [isAddParticipantToCallFormOpen, setIsAddParticipantToCallFormOpen] =
      useState<boolean>(false);
   const [isActionFormOpen, setIsActionFormOpen] = useState<boolean>(false);
   const [isAddSummaryFormOpen, setIsAddSummaryFormOpen] = useState<boolean>(false);
   const [savedNote, setSavedNote] = useState<string>("");

   const intl = useIntl();
   const callData = getDataFromFormTabs(tabs);

   const toggleButtonState = (buttonName: keyof typeof activeButtons) => {
      setActiveButtons((prev) => ({ ...prev, [buttonName]: !prev[buttonName] }));
   };

   const handleOpenForm = (formId: number, dataId?: number) => {
      setFormIds({ formId, dataId: dataId ?? null });
      setIsActionFormOpen(true);
   };

   const userInfo: CallManagerInfo[] = [
      { label: "19-420 Kowale oleckie, Stożne 23", icon: <Icon_MapMarker /> },
      {
         label: `${intl.formatMessage({ id: "callManager.NIP" })}: 847-000-00-71`,
         icon: <Icon_NIP />,
      },
      {
         label: `${intl.formatMessage({ id: "callManager.clientNumber" })}: ${callData.client_id}`,
         icon: <Icon_PhoneCallUser />,
      },
      {
         label: `${intl.formatMessage({ id: "callManager.phoneCallTopic" })}: ${callData.case_subject || intl.formatMessage({ id: "callManager.noTopic" })}`,
         icon: <Icon_ConversationTopic />,
      },
   ];

   const userActions: CallManagerButton[] = [
      {
         label: "callManager.addNote",
         onClick: () => setIsAddSummaryFormOpen(true),
         icon: <Icon_AddNote />,
         disabled: false,
      },
      {
         label: "callManager.details",
         onClick: () => handleOpenForm(callData.details_form_id, callData.details_data_id),
         icon: <Icon_Details />,
         disabled: false,
      },
      {
         label: "callManager.addTask",
         onClick: () => handleOpenForm(callData.add_task_form_id),
         icon: <Icon_AddTask />,
         disabled: true,
      },
      {
         label: "callManager.reschedulePhoneCall",
         onClick: () => {},
         icon: <Icon_Reschedule />,
         disabled: false,
      },
      {
         label: "callManager.more",
         onClick: () => {
            setIsMoreUserActionsOpen((prev) => !prev);
            toggleButtonState("moreActions");
         },
         icon: <Icon_MoreActions fill={activeButtons.moreActions ? "#fff" : "#1f1f1f"} />,
         isActive: activeButtons.moreActions,
      },
   ];

   const moreUserActions: CallManagerButton[] = [
      {
         label: "callManager.addParticipant",
         onClick: () => setIsAddParticipantToCallFormOpen(true),
         icon: <Icon_AddUserToCall />,
      },
      {
         label: "callManager.manageParticipants",
         onClick: () => setIsParticipantListOpen(true),
         icon: <Icon_ManageUsers />,
      },
      {
         label: "callManager.swap",
         onClick: undefined,
         icon: <Icon_SwapCalls />,
         disabled: true,
      },
      {
         label: "callManager.merge",
         onClick: undefined,
         icon: <Icon_MergeCalls />,
         disabled: true,
      },
   ];

   const phoneCallActions: CallManagerButton[] = [
      {
         label: "callManager.endPhoneCall",
         icon: <Icon_HangUpPhoneCall />,
         onClick: () => setCallStep(CallStep.COMPLETED),
      },
      {
         label: "callManager.suspendPhoneCall",
         icon: activeButtons.suspendPhoneCall ? <Icon_ContinueCall /> : <Icon_OnHold />,
         onClick: () => toggleButtonState("suspendPhoneCall"),
         isActive: activeButtons.suspendPhoneCall,
      },
      {
         label: "callManager.bluetooth",
         icon: <Icon_Bluetooth />,
         onClick: undefined,
         disabled: true,
      },
      {
         label: "callManager.speaker",
         icon: activeButtons.speaker ? <Icon_Speaker fill="#fff" /> : <Icon_Speaker fill="#333" />,
         onClick: () => toggleButtonState("speaker"),
         isActive: activeButtons.speaker,
      },
      {
         label: "callManager.mute",
         icon: activeButtons.muteMicrophone ? (
            <Icon_MuteMicrophone fill="#fff" />
         ) : (
            <Icon_Microphone />
         ),
         onClick: () => toggleButtonState("muteMicrophone"),
         isActive: activeButtons.muteMicrophone,
      },
   ];

   const users = [
      { name: "User 1", number: "+48 554445667" },
      { name: "User 2", number: "+48 557445667" },
      { name: "User 3", number: "+48 556445667" },
      { name: "", number: "+48 556445687" },
   ];

   const basicUserInfo = (
      <>
         <div
            className={clsx(
               "call-manager__user-basic-info",
               callStep === CallStep.COMPLETED && "call-manager__user-basic-info--smaller-margin",
            )}
         >
            <img src={Client} alt="User image" className="call-manager__user-image" />
            <h1 className="call-manager__username">
               {callData.kos_name || intl.formatMessage({ id: "callManager.noUserName" })}
            </h1>
            <h3 className="call-manager__phone-number">{callData.phone_number}</h3>
            {callStep === CallStep.ACTIVE && (
               <p className="call-manager__call-status">{callStatus}</p>
            )}
         </div>
         {callStep !== CallStep.COMPLETED && (
            <>
               <ul className="call-manager__list-info">
                  {userInfo.map(({ label, icon }) => (
                     <li key={label} className="call-manager__list-item">
                        <div className="call-manager__list-item-icon">{icon}</div>
                        {label}
                     </li>
                  ))}
               </ul>
               <div
                  className={clsx(
                     "call-manager__note-from-last-call",
                     callStep === CallStep.PLANNED &&
                        "call-manager__note-from-last-call--smaller-margin",
                  )}
               >
                  <h3>{intl.formatMessage({ id: "callManager.noteFromLastCall" })}</h3>
                  <p>
                     {callData.note
                        ? callData.note
                        : intl.formatMessage({ id: "callManager.noNoteFromLastCall" })}
                  </p>
               </div>
            </>
         )}
      </>
   );

   const renderModalBasedOnCallStep = (step: CallStep) => {
      if (isAddSummaryFormOpen) {
         return (
            <AddSummaryForm
               data={callData}
               parentId={parentId}
               setIsAddSummaryFormOpen={setIsAddSummaryFormOpen}
               isAddSummaryFormOpen={isAddSummaryFormOpen}
               savedNote={savedNote}
               setSavedNote={setSavedNote}
            />
         );
      }

      switch (step) {
         case CallStep.ACTIVE:
            return isParticipantListOpen ? (
               <ParticipantList users={users} />
            ) : isAddParticipantToCallFormOpen ? (
               <AddNewParticipantToPhoneCall users={users} />
            ) : (
               <>
                  {basicUserInfo}
                  <ButtonGroup buttons={userActions} className="call-manager__user-actions" />
                  <AnimatePresence>
                     {isMoreUserActionsOpen && (
                        <motion.div
                           className="call-manager__user-actions call-manager__user-actions--more"
                           initial={{ opacity: 0, y: -10 }}
                           animate={{ opacity: 1, y: 0 }}
                           exit={{ opacity: 0, y: -10 }}
                           transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                           {moreUserActions.map((action) => (
                              <ActionButton key={action.label} {...action} />
                           ))}
                        </motion.div>
                     )}
                  </AnimatePresence>
                  <ButtonGroup
                     buttons={phoneCallActions}
                     className="call-manager__phone-call-actions"
                     isPhoneCallActions
                  />
               </>
            );

         case CallStep.PLANNED:
            return (
               <>
                  {basicUserInfo}
                  <ActionButton
                     icon={<Icon_CallUser />}
                     label="callManager.startCall"
                     disabled={false}
                     onClick={() => setCallStep(CallStep.ACTIVE)}
                     className="call-manager__action-button--start-call"
                  />
               </>
            );

         case CallStep.COMPLETED:
            return (
               <>
                  <p>{intl.formatMessage({ id: "callManager.youTalkedWith" })}</p>
                  {basicUserInfo}
                  <p className="call-manager__list-item">
                     <div className="call-manager__list-item-icon">
                        {<Icon_ConversationTopic />}
                     </div>
                     {intl.formatMessage({ id: "callManager.phoneCallTopic" })}:{" "}
                     {callData.case_subject}
                  </p>
                  <button
                     className="call-manager__completed-step-button"
                     onClick={() => setIsAddSummaryFormOpen(true)}
                  >
                     <Icon_AddNote /> {intl.formatMessage({ id: "callManager.addSummary" })}
                  </button>
                  <button
                     disabled
                     className="call-manager__completed-step-button"
                     onClick={() =>
                        handleOpenForm(
                           callData.schedule_next_call_form_id,
                           callData.schedule_next_call_data_id,
                        )
                     }
                  >
                     <Icon_AddTask />{" "}
                     {intl.formatMessage({ id: "callManager.scheduleNextMeeting" })}
                  </button>
               </>
            );
      }
   };

   return (
      <Modal
         isOpen={isCallManagerOpen}
         onCancel={() => setIsCallManagerOpen(false)}
         width={600}
         wrapClassName="call-manager__modal"
         topBarContent={
            (isParticipantListOpen || isAddParticipantToCallFormOpen || isAddSummaryFormOpen) && (
               <p className="call-manager__back-button">
                  <Icon_GoBack
                     fill="#333333"
                     onClick={() => {
                        setIsParticipantListOpen(false);
                        setIsAddParticipantToCallFormOpen(false);
                        setIsAddSummaryFormOpen(false);
                     }}
                  />
               </p>
            )
         }
      >
         <AnimatePresence>
            <motion.div
               key="call-manager"
               className="call-manager"
               initial={{ opacity: 0, y: 20, scale: 0.98 }}
               animate={{ opacity: 1, y: 0, scale: 1 }}
               exit={{ opacity: 0, y: 20, scale: 0.98 }}
               transition={{ duration: 0.3, ease: "easeInOut" }}
            >
               <div className="call-manager__user-info-container">
                  {renderModalBasedOnCallStep(callStep)}
               </div>
            </motion.div>
         </AnimatePresence>

         {isActionFormOpen && formIds.formId != null && (
            <Modal
               isOpen={isActionFormOpen}
               onCancel={() => setIsActionFormOpen(false)}
               width={600}
               wrapClassName="call-manager__modal"
            >
               <FormComplete
                  formId={formIds.formId}
                  dataId={formIds.dataId ?? undefined}
                  isSubmitVisible={false}
               />
            </Modal>
         )}
      </Modal>
   );
};
